import { message, Modal, Spin } from "antd";
import { useEffect, useState } from "react";

import { updateLoanProfile, uploadFile } from "services/LoanService";

import CustomSelectMain from "layout/SelectInput";
import TextInput from "components/TextInput";

// import certData, { levelData, levelDataND } from "../certData";
import { uniData } from "../Apply/uniData";
import certData, { levelData, levelDataND } from "../Apply/certData";


export default function LoanProfileEditModal({
  open,
  section,
  profile,
  onClose,
  onUpdated,
}) {
  const [loading, setLoading] = useState(false);

  /**
   * Files selected by the user but not uploaded yet.
   *
   * {
   *   admission_letter: File,
   *   school_fee_receipt: File
   * }
   */
  const [pendingFiles, setPendingFiles] = useState({});

  /**
   * Local form state.
   */
  const [form, setForm] = useState({});

  /**
   * Reset/populate form whenever the modal/section/profile changes.
   */
  useEffect(() => {
    if (!open || !profile || !section) return;

    setPendingFiles({});

    switch (section) {
      case "bank_account":
        setForm({
          account_name: profile.bank_account?.account_name || "",
          account_number: profile.bank_account?.account_number || "",
          bank_name: profile.bank_account?.bank_name || "",
          bank_code: profile.bank_account?.bank_code || "",
        });
        break;

      case "verification":
        setForm({
          bvn: profile.bvn || "",
          employment_status: profile.employment_status || "",
          certificate: profile.certificate || "",
          othercertificate: profile.othercertificate || "",
        });
        break;

      case "institution":
        setForm({
          name: profile.institution?.name || "",
          othername: profile.institution?.othername || "",
          course: profile.institution?.course || "",
          faculty: profile.institution?.faculty || "",
          matric_number: profile.institution?.matric_number || "",
          study_year:
            profile.institution?.study_year ||
            profile.institution?.current_study_year ||
            "",
          graduation_year: normalizeGraduationYear(
            profile.institution?.graduation_year,
          ),
          certificate: profile.certificate || "",
          othercertificate: profile.othercertificate || "",
        });
        break;

      case "guarantor_one":
        setForm({
          first_name: profile.guarantor_one?.first_name || "",
          middle_name: profile.guarantor_one?.middle_name || "",
          surname: profile.guarantor_one?.surname || "",
          phone: profile.guarantor_one?.phone || "",
          work_place: profile.guarantor_one?.work_place || "",
          email: profile.guarantor_one?.email || "",
          nin: profile.guarantor_one?.nin || "",
        });
        break;

      case "guarantor_two":
        setForm({
          first_name: profile.guarantor_two?.first_name || "",
          middle_name: profile.guarantor_two?.middle_name || "",
          surname: profile.guarantor_two?.surname || "",
          phone: profile.guarantor_two?.phone || "",
          work_place: profile.guarantor_two?.work_place || "",
          email: profile.guarantor_two?.email || "",
          nin: profile.guarantor_two?.nin || "",
        });
        break;

      case "documents":
        setForm({
          admission_letter: profile.admission_letter || "",
          school_fee_receipt: profile.school_fee_receipt || "",
          certificate: profile.certificate || "",
          othercertificate: profile.othercertificate || "",
        });
        break;

      default:
        setForm({});
    }
  }, [open, section, profile]);

  /**
   * Backend may return:
   *
   * 2029
   * 01-2022
   * 2022-01
   * etc.
   *
   * <input type="month" /> requires YYYY-MM.
   */
  function normalizeGraduationYear(value) {
    if (!value) return "";

    const stringValue = String(value);

    /**
     * Already correct.
     */
    if (/^\d{4}-\d{2}$/.test(stringValue)) {
      return stringValue;
    }

    /**
     * DD-MM-YYYY / MM-YYYY
     */
    const match = stringValue.match(/^(?:\d{1,2}-)?(\d{4})$/);

    if (match) {
      return `${match[1]}-01`;
    }

    /**
     * YYYY only.
     */
    if (/^\d{4}$/.test(stringValue)) {
      return `${stringValue}-01`;
    }

    /**
     * Try Date parsing as a fallback.
     */
    const date = new Date(value);

    if (!Number.isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");

      return `${year}-${month}`;
    }

    return "";
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  /**
   * Keep the File object locally.
   *
   * IMPORTANT:
   * We intentionally do NOT call uploadFile() here.
   */
  function handleFileChange(e, field) {
    const file = e.target.files?.[0];

    if (!file) return;

    setPendingFiles((current) => ({
      ...current,
      [field]: file,
    }));

    /**
     * Used for showing a local preview/name.
     */
    setForm((current) => ({
      ...current,
      [`${field}_pending`]: true,
    }));

    /**
     * Allows selecting the same file again.
     */
    e.target.value = "";
  }

  function removePendingFile(field) {
    setPendingFiles((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });

    setForm((current) => {
      const next = { ...current };
      delete next[`${field}_pending`];
      return next;
    });
  }

  async function uploadPendingFile(file) {
    const formData = new FormData();

    formData.append("upload", file);

    const fileUrl = await uploadFile(formData);

    if (!fileUrl) {
      throw new Error("Unable to upload file.");
    }

    return fileUrl;
  }

  function isImage(url = "") {
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
  }

  function isPdf(url = "") {
    return /\.pdf(\?.*)?$/i.test(url);
  }

  function getFileName(url = "") {
    try {
      const pathname = new URL(url).pathname;
      const name = pathname.split("/").pop();

      return decodeURIComponent(name || "Uploaded document");
    } catch {
      return "Uploaded document";
    }
  }

  function getPendingFileName(file) {
    if (!file) return "";

    return file.name || "Selected document";
  }

  function getTitle() {
    switch (section) {
      case "bank_account":
        return "Bank Account";

      case "verification":
        return "Verification & Employment";

      case "institution":
        return "Institution";

      case "guarantor_one":
        return "Guarantor One";

      case "guarantor_two":
        return "Guarantor Two";

      case "documents":
        return "Documents";

      default:
        return "Loan Profile";
    }
  }

  /**
   * Generic input.
   */
  function renderInput({
    label,
    name,
    type = "text",
    required = false,
    placeholder = "",
  }) {
    return (
      <TextInput
        label={label}
        name={name}
        id={name}
        type={type}
        placeholder={placeholder}
        value={form[name] || ""}
        onChange={handleChange}
        astheric={required}
      />
    );
  }

  /**
   * Institution select.
   */
  function renderInstitutionSelect() {
    return (
      <CustomSelectMain
        astheric
        label="Name of Institution"
        name="name"
        value={form.name || ""}
        onChange={handleChange}
      >
        <option value="">Select Institution</option>

        {uniData?.map((uni) => (
          <option className="active-option" key={uni.id} value={uni.value}>
            {uni.label}
          </option>
        ))}
      </CustomSelectMain>
    );
  }

  /**
   * Study year / programme select.
   */
  function renderStudyYearSelect() {
    const studyLevelData = [...levelData, ...levelDataND];

    return (
      <CustomSelectMain
        astheric
        label="Current Year Of Study"
        name="study_year"
        value={form.study_year || ""}
        onChange={handleChange}
      >
        <option value="">Select Current Year Of Study</option>

        {studyLevelData.map((local) => (
          <option className="active-option" key={local.id} value={local.value}>
            {local.label}
          </option>
        ))}
      </CustomSelectMain>
    );
  }

  /**
   * Certificate select.
   */
  function renderCertificateSelect() {
    return (
      <CustomSelectMain
        astheric
        label="Certificate In View"
        name="certificate"
        value={form.certificate || ""}
        onChange={handleChange}
      >
        <option value="">Select Certificate</option>

        {certData?.map((local) => (
          <option className="active-option" key={local.id} value={local.value}>
            {local.label}
          </option>
        ))}
      </CustomSelectMain>
    );
  }

  /**
   * Remote document preview.
   */
  function renderFilePreview(url) {
    if (!url) return null;

    if (isImage(url)) {
      return (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-500 mb-2">Current file</p>

          <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
            <img
              src={url}
              alt="Uploaded document"
              className="w-full max-h-48 object-contain"
            />
          </div>

          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-700 hover:underline"
          >
            <i className="bi bi-eye" />
            View full document
          </a>
        </div>
      );
    }

    if (isPdf(url)) {
      return (
        <div className="mt-3 border border-gray-200 rounded-lg bg-gray-50 p-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
              <i className="bi bi-file-earmark-pdf text-xl" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-700 truncate">
                {getFileName(url)}
              </p>

              <p className="text-[11px] text-gray-400">PDF document</p>
            </div>

            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-primary-700 hover:underline"
            >
              Preview
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-3 border border-gray-200 rounded-lg bg-gray-50 p-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
            <i className="bi bi-file-earmark text-xl" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-700 truncate">
              {getFileName(url)}
            </p>

            <p className="text-[11px] text-gray-400">Uploaded document</p>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-primary-700 hover:underline"
          >
            Open
          </a>
        </div>
      </div>
    );
  }

  /**
   * Local file preview.
   *
   * This DOES NOT upload anything.
   */
  function renderPendingFile(file, field) {
    if (!file) return null;

    const isImageFile = file.type?.startsWith("image/");

    return (
      <div className="mt-3 border border-primary-100 rounded-lg bg-primary-50 p-3">
        <div className="flex items-center gap-3">
          {isImageFile ? (
            <img
              src={URL.createObjectURL(file)}
              alt={file.name}
              className="w-12 h-12 rounded-lg object-cover border border-gray-200"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-white text-primary-700 flex items-center justify-center">
              <i className="bi bi-file-earmark text-xl" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-700 truncate">
              {getPendingFileName(file)}
            </p>

            <p className="text-[11px] text-primary-600">
              Ready to upload when you save
            </p>
          </div>

          <button
            type="button"
            onClick={() => removePendingFile(field)}
            className="text-xs text-red-600 hover:text-red-700"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  /**
   * File input.
   */
  function renderFileInput({ label, name, accept = ".pdf,.jpg,.jpeg,.png" }) {
    const existingUrl = form[name];

    const pendingFile = pendingFiles[name];

    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          {label}
        </label>

        <div className="relative border-2 border-dashed border-gray-300 rounded-xl p-4 transition hover:border-primary-400 hover:bg-gray-50">
          <input
            type="file"
            accept={accept}
            disabled={loading}
            onChange={(e) => handleFileChange(e, name)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          />

          <div className="text-center pointer-events-none">
            <div className="w-11 h-11 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
              <i className="bi bi-cloud-arrow-up text-xl text-gray-500" />
            </div>

            <p className="mt-2 text-sm font-medium text-gray-700">
              {pendingFile
                ? "Replace selected document"
                : existingUrl
                  ? "Replace document"
                  : "Upload document"}
            </p>

            <p className="text-xs text-gray-400 mt-1">
              Click to browse or drag and drop
            </p>

            <p className="text-[11px] text-gray-400 mt-1">
              PDF, JPG, JPEG or PNG
            </p>
          </div>
        </div>

        {pendingFile
          ? renderPendingFile(pendingFile, name)
          : existingUrl
            ? renderFilePreview(existingUrl)
            : null}
      </div>
    );
  }

  function renderGuarantorForm() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {renderInput({
          label: "First Name",
          name: "first_name",
          required: true,
        })}

        {renderInput({
          label: "Middle Name",
          name: "middle_name",
        })}

        {renderInput({
          label: "Surname",
          name: "surname",
          required: true,
        })}

        {renderInput({
          label: "Phone",
          name: "phone",
          required: true,
        })}

        {renderInput({
          label: "Email",
          name: "email",
          type: "email",
        })}

        {renderInput({
          label: "Work Place",
          name: "work_place",
        })}

        {renderInput({
          label: "NIN",
          name: "nin",
        })}
      </div>
    );
  }

  function renderInstitutionForm() {
    return (
      <div className="grid grid-cols-1 gap-4">
        {renderInstitutionSelect()}

        {form.name?.toLowerCase() === "others" &&
          renderInput({
            label: "Specify Institution",
            name: "othername",
            required: true,
            placeholder: "Name of Institution",
          })}

        {renderInput({
          label: "Course Of Study",
          name: "course",
          required: true,
          placeholder: "Chemistry",
        })}

        {renderInput({
          label: "Faculty",
          name: "faculty",
          required: true,
          placeholder: "Science",
        })}

        {renderInput({
          label: "Mat. No",
          name: "matric_number",
          placeholder: "Matriculation Number",
        })}

        {renderInput({
          label: "Year of Graduation",
          name: "graduation_year",
          type: "month",
          required: true,
        })}

        {renderStudyYearSelect()}

        {renderCertificateSelect()}

        {form.certificate?.toLowerCase() === "others" &&
          renderInput({
            label: "Specify Certificate",
            name: "othercertificate",
            required: true,
            placeholder: "ICAN, HSE etc",
          })}
      </div>
    );
  }

  function renderContent() {
    switch (section) {
      case "bank_account":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderInput({
              label: "Account Name",
              name: "account_name",
              required: true,
            })}

            {renderInput({
              label: "Account Number",
              name: "account_number",
              required: true,
            })}

            {renderInput({
              label: "Bank Name",
              name: "bank_name",
              required: true,
            })}

            {renderInput({
              label: "Bank Code",
              name: "bank_code",
            })}
          </div>
        );

      case "verification":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderInput({
              label: "BVN",
              name: "bvn",
              required: true,
            })}

            {renderInput({
              label: "Employment Status",
              name: "employment_status",
            })}

            {renderInput({
              label: "Certificate",
              name: "certificate",
            })}

            {renderInput({
              label: "Other Certificate",
              name: "othercertificate",
            })}
          </div>
        );

      case "institution":
        return renderInstitutionForm();

      case "guarantor_one":
      case "guarantor_two":
        return renderGuarantorForm();

      case "documents":
        return (
          <div className="space-y-5">
            {renderFileInput({
              label: "Admission Letter",
              name: "admission_letter",
            })}

            {renderFileInput({
              label: "School Fee Receipt",
              name: "school_fee_receipt",
            })}

            {renderFileInput({
              label: "Certificate",
              name: "certificate",
            })}

            {renderFileInput({
              label: "Other Certificate",
              name: "othercertificate",
            })}
          </div>
        );

      default:
        return (
          <div className="py-8 text-center text-gray-500">
            Invalid profile section.
          </div>
        );
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setLoading(true);

    try {
      /**
       * ----------------------------------------
       * 1. Upload only now
       * ----------------------------------------
       *
       * This means simply selecting a file does
       * NOT consume storage.
       */
      const uploadedFiles = {};

      for (const [field, file] of Object.entries(pendingFiles)) {
        if (!file) continue;

        uploadedFiles[field] = await uploadPendingFile(file);
      }

      /**
       * ----------------------------------------
       * 2. Build payload
       * ----------------------------------------
       */
      let payload = {};

      switch (section) {
        case "bank_account":
          payload = {
            bank_account: {
              account_name: form.account_name,
              account_number: form.account_number,
              bank_name: form.bank_name,
              bank_code: form.bank_code,
            },
          };
          break;

        case "verification":
          payload = {
            bvn: form.bvn,
            employment_status: form.employment_status,
            certificate: form.certificate,
            othercertificate: form.othercertificate,
          };
          break;

        case "institution":
          payload = {
            institution: {
              name: form.name,
              othername: form.othername,
              course: form.course,
              faculty: form.faculty,
              matric_number: form.matric_number,
              study_year: form.study_year,
              current_study_year: form.study_year,
              graduation_year: form.graduation_year,
            },

            certificate: form.certificate,
            othercertificate: form.othercertificate,
          };
          break;

        case "guarantor_one":
          payload = {
            guarantor_one: {
              first_name: form.first_name,
              middle_name: form.middle_name,
              surname: form.surname,
              phone: form.phone,
              work_place: form.work_place,
              email: form.email,
              nin: form.nin,
            },
          };
          break;

        case "guarantor_two":
          payload = {
            guarantor_two: {
              first_name: form.first_name,
              middle_name: form.middle_name,
              surname: form.surname,
              phone: form.phone,
              work_place: form.work_place,
              email: form.email,
              nin: form.nin,
            },
          };
          break;

        case "documents":
          payload = {
            admission_letter:
              uploadedFiles.admission_letter || form.admission_letter,

            school_fee_receipt:
              uploadedFiles.school_fee_receipt || form.school_fee_receipt,

            certificate: uploadedFiles.certificate || form.certificate,

            othercertificate:
              uploadedFiles.othercertificate || form.othercertificate,
          };
          break;

        default:
          return;
      }

      /**
       * ----------------------------------------
       * 3. Update backend
       * ----------------------------------------
       */
      const res = await updateLoanProfile(payload);

      const updatedProfile = res?.data?.data;

      message.success("Loan profile updated successfully.");

      if (updatedProfile) {
        onUpdated(updatedProfile);
      } else {
        onClose();
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update loan profile.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={loading ? undefined : onClose}
      footer={null}
      centered
      destroyOnClose
      width={650}
    >
      <div className="font-poppins">
        <header className="mb-6">
          <h2 className="text-xl font-semibold text-primary">
            Edit {getTitle()}
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Update this information for your loan application.
          </p>
        </header>

        <form onSubmit={handleSubmit}>
          {renderContent()}

          <footer className="flex justify-end gap-3 mt-8 pt-5 border-t border-gray-100">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-800 text-white hover:bg-primary-900 disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Spin size="small" />
                  Saving...
                </span>
              ) : (
                "Save Changes"
              )}
            </button>
          </footer>
        </form>
      </div>
    </Modal>
  );
}
